/**
 * SubscribeToChatUpdatesUseCase
 *
 * Live-update use case for chat messages. Wraps the live subscription on the
 * `message_nodes/{groupChatId}/messages` collection with E2EE decryption and
 * fans each delivered change out to a per-change-type handler
 * (`onAdded` / `onModified` / `onRemoved`).
 *
 * Flutter source: lib/presentation/screens/chat/chat_screen.dart:500-655
 * (`listenToChatUpdates`).
 *   - Query: `where("timestamp", isGreaterThan: timestampChatObservation).snapshots()`
 *     (`chat_screen.dart:508-514`). Handled by `ChatService.subscribeToNewMessages`.
 *   - `chat_screen.dart:516-537` — for EVERY change (added / modified /
 *     removed), the doc is parsed via `ChatMessage.fromJson` and if
 *     `content != null` it is decrypted BEFORE the change-type fan-out. The
 *     web port mirrors that ordering exactly — decrypt first, dispatch second
 *     — and narrows only the eligibility check to
 *     `ChatUtils.shouldDecryptMessageContent`, which owns that decision (type
 *     list, rationale and Flutter anchors live on that method). The `removed`
 *     branch in Flutter never reads `content`, but the decryption still runs
 *     because the Flutter code path is change-type-agnostic. We keep the same
 *     shape (and so the handler implementations can rely on `content` already
 *     being plaintext if they choose to read it).
 *   - `chat_screen.dart:539`, `:614`, `:640` — dispatch based on
 *     `DocumentChangeType.added` / `.modified` / `.removed`.
 *   - The presentation-side concerns (insert at index 0, sort, scroll, mark
 *     seen, unread badge, app-lifecycle gating) live in `chat_screen.dart:539-653`
 *     and stay in the feature hook on web. This use case is intentionally a
 *     thin domain pipe: decrypt + route. No list mutation, no sort, no scroll,
 *     no Redux dispatch.
 *
 * Pure-domain contract:
 *   - The use case calls `DecryptTextUseCase` internally — no callback args.
 *     The caller is responsible for having previously invoked
 *     `InitializeTextEncryptionUseCase.execute(peer.publicJwk)` so the
 *     singleton's derived key is bound to the intended peer.
 *   - The use case does NOT touch `firestoreService` directly and does NOT
 *     dispatch to Redux — both are presentation concerns.
 *   - The DTO → Entity bridge goes through `ChatMessageRemapper.fromDto`, the
 *     canonical bridge introduced in Task 8's fix.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { ChatService } from '@/domain/services/chat/chatService';
import { DecryptTextUseCase, decryptTextUseCase } from '@/domain/usecases/textEncryption/decryptTextUseCase';
import type { ChatMessageChangeDto } from '@/data/dtos/chat/chatMessageChangeDto';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import { ChatMessageRemapper } from '@/domain/remappers/chat/chatMessageRemapper';
import { ChatUtils } from '@/domain/utils/chatUtils';

/**
 * Per-change-type handler bundle. Each handler receives a fully-decrypted
 * `ChatMessageEntity` (i.e. `content`, when present on the DTO, has already
 * been run through `decryptTextUseCase.execute`).
 */
export interface ChatUpdateHandlers {
  onAdded: (message: ChatMessageEntity) => void;
  onModified: (message: ChatMessageEntity) => void;
  onRemoved: (message: ChatMessageEntity) => void;
}

export class SubscribeToChatUpdatesUseCase {
  private chatService: ChatService;
  private decryptTextUseCase: DecryptTextUseCase;

  constructor(
    chatService: ChatService = ChatService.getInstance(),
    decryptUseCase: DecryptTextUseCase = decryptTextUseCase,
  ) {
    this.chatService = chatService;
    this.decryptTextUseCase = decryptUseCase;
  }

  /**
   * Subscribe to live chat-message updates after `sinceTimestamp`. For each
   * delivered change, the message's `content` (when defined) is decrypted, the
   * DTO is bridged to a `ChatMessageEntity` via `ChatMessageRemapper.fromDto`,
   * and the appropriate per-change-type handler is invoked.
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param sinceTimestamp - Lower-exclusive cursor; the snapshot does NOT
   *   re-deliver any doc with `timestamp <= sinceTimestamp` (matches
   *   `chat_screen.dart:503-506` — Flutter uses the last fetched message's
   *   timestamp, falling back to `now` when the chat is empty).
   * @param handlers - Per-change-type handlers. Receives the decrypted entity.
   * @returns The Firestore `Unsubscribe` handle from `ChatService.subscribeToNewMessages`.
   *   The caller (the feature hook) is responsible for invoking it on cleanup.
   */
  execute(
    groupChatId: string,
    sinceTimestamp: number,
    handlers: ChatUpdateHandlers,
  ): Unsubscribe {
    return this.chatService.subscribeToNewMessages(
      groupChatId,
      sinceTimestamp,
      (changes: ChatMessageChangeDto[]) => {
        // Each snapshot batch may carry multiple changes. Flutter iterates
        // them serially (`chat_screen.dart:517`); we mirror that with a
        // serial async loop so handler invocation order is preserved per
        // batch (e.g. an `added` and a `modified` arriving in the same
        // snapshot are dispatched in arrival order, exactly as in Flutter).
        void this.processChanges(changes, handlers);
      },
    );
  }

  /**
   * Decrypts each change's `content` (when set) and dispatches to the
   * matching handler. Serial — see the call-site comment.
   *
   * Errors from the decrypt use case propagate out of the async function; they
   * are not silently swallowed. This matches the Flutter behavior — Flutter's
   * `await _e2ee.decrypt(...)` (`chat_screen.dart:534`) propagates inside the
   * `_streamSubscription.listen` async callback as well. The presentation
   * layer is responsible for any user-facing error handling.
   */
  private async processChanges(
    changes: ChatMessageChangeDto[],
    handlers: ChatUpdateHandlers,
  ): Promise<void> {
    for (const change of changes) {
      const { type, data: dto } = change;

      // Decrypt gate — `ChatUtils.shouldDecryptMessageContent`, the SAME
      // predicate `FetchChatMessagesUseCase` (the paginated read) uses, so a
      // shared caption arriving live decrypts exactly as it does on reload.
      // Type list, share-caption rationale and the Flutter no-type-gate
      // divergence are documented on that method.
      const decryptedContent = ChatUtils.shouldDecryptMessageContent(dto.type, dto.content)
        ? await this.decryptTextUseCase.execute(dto.content!)
        : undefined;

      const entity = ChatMessageRemapper.fromDto(dto);
      if (decryptedContent !== undefined) entity.content = decryptedContent;

      // Fan out per change type — chat_screen.dart:539, :614, :640.
      switch (type) {
        case 'added':
          handlers.onAdded(entity);
          break;
        case 'modified':
          handlers.onModified(entity);
          break;
        case 'removed':
          handlers.onRemoved(entity);
          break;
      }
    }
  }
}
